// components/models/QwenImage21ControlPanel.tsx
import React, { useCallback } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import RangeControl from '../ui/RangeControl';
import { AppTooltip } from '../ui/tooltip';
import { QwenImage21Params, QwenImage21Resolution } from '#types/types';
import {
  QWEN_IMAGE_21_DEFAULT_CFG,
  QWEN_IMAGE_21_DEFAULT_RESOLUTION,
  QWEN_IMAGE_21_DEFAULT_STEPS,
  QWEN_IMAGE_21_MAX_CFG,
  QWEN_IMAGE_21_MAX_STEPS,
  QWEN_IMAGE_21_MIN_CFG,
  QWEN_IMAGE_21_MIN_NOISE_LEVEL,
  QWEN_IMAGE_21_RESOLUTION_OPTIONS,
} from '#lib/qwen-image-21';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

export const getDefaultQwenImage21Params = (): QwenImage21Params => ({
  prompt: '',
  resolution: QWEN_IMAGE_21_DEFAULT_RESOLUTION,
  steps: QWEN_IMAGE_21_DEFAULT_STEPS,
  cfg: QWEN_IMAGE_21_DEFAULT_CFG,
  negativePrompt: '',
  noiseLevel: 1,
  cfgOneLastSteps: 0,
  seed: -1,
});

const TOOLTIP_CLASS = 'max-w-[320px] border border-white/[0.06] bg-[#353535] px-3 py-2 text-xs leading-relaxed text-white/75 shadow-2xl';
const STEPS_TIP = 'Fewer steps are faster, more steps refine detail.';
const CFG_TIP = '3 draws people and small faces properly. 1 halves the run time but leaves them malformed.';
const CFG_ONE_LAST_STEPS_TIP = 'These last steps run at CFG 1, without the negative prompt.';
const NOISE_LEVEL_TIP = '1 starts from pure noise. Just below 1 (0.985) keeps the source layout.';

const QwenImage21ControlPanel: React.FC = React.memo(() => {
  const { kernel } = useKernel();
  const params = useKernelStore(
    state => state.modelParams[state.diffusionModel] as QwenImage21Params | undefined,
  );

  const updateParam = useCallback(<K extends keyof QwenImage21Params>(
    name: K,
    value: QwenImage21Params[K],
  ) => {
    const diffusionModel = kernel.current?.getDiffusionModel();
    if (!diffusionModel) return;
    kernel.current?.setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) return prevModelParams;
      return {
        ...prevModelParams,
        [diffusionModel]: { ...currentParams, [name]: value },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      ...(name === 'prompt' || name === 'negativePrompt' ? { value_length: String(value).length } : { value }),
      model_name: diffusionModel,
      source_component: 'QwenImage21ControlPanel',
    });
  }, [kernel]);

  const handlePromptChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    updateParam('prompt', event.target.value);
  }, [updateParam]);

  const handleResolutionChange = useCallback((value: string) => {
    updateParam('resolution', value as QwenImage21Resolution);
  }, [updateParam]);

  const handleStepsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    updateParam('steps', Math.round(Number(event.target.value)));
  }, [updateParam]);

  const handleCfgChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    updateParam('cfg', Number(event.target.value));
  }, [updateParam]);

  const handleNegativePromptChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateParam('negativePrompt', event.target.value);
  }, [updateParam]);

  const handleCfgOneLastStepsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    updateParam('cfgOneLastSteps', Math.round(Number(event.target.value)));
  }, [updateParam]);

  const handleNoiseLevelChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    updateParam('noiseLevel', Number(event.target.value));
  }, [updateParam]);

  if (!params) return null;

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe what to change and what to keep..."
          name="prompt"
          value={params.prompt}
          onChange={handlePromptChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-white mb-1">Resolution</label>
        <Select
          id="qwen-image-21-resolution"
          value={params.resolution ?? QWEN_IMAGE_21_DEFAULT_RESOLUTION}
          options={QWEN_IMAGE_21_RESOLUTION_OPTIONS}
          onChange={handleResolutionChange}
        />
      </div>

      <AppTooltip content={STEPS_TIP} side="left" contentClassName={TOOLTIP_CLASS}>
        <div className="mb-4">
          <RangeControl
            label="Steps"
            name="steps"
            min={1}
            max={QWEN_IMAGE_21_MAX_STEPS}
            step={1}
            value={params.steps ?? QWEN_IMAGE_21_DEFAULT_STEPS}
            onChange={handleStepsChange}
          />
        </div>
      </AppTooltip>

      <AppTooltip content={CFG_TIP} side="left" contentClassName={TOOLTIP_CLASS}>
        <div className="mb-4">
          <RangeControl
            label="CFG"
            name="cfg"
            min={QWEN_IMAGE_21_MIN_CFG}
            max={QWEN_IMAGE_21_MAX_CFG}
            step={0.5}
            value={params.cfg ?? QWEN_IMAGE_21_DEFAULT_CFG}
            onChange={handleCfgChange}
          />
        </div>
      </AppTooltip>

      {(params.cfg ?? QWEN_IMAGE_21_DEFAULT_CFG) > 1 && (
        <>
          <div className="mb-4">
            <AutoGrowingTextarea
              label="Negative Prompt"
              placeholder="Describe what to avoid..."
              name="negativePrompt"
              value={params.negativePrompt ?? ''}
              onChange={handleNegativePromptChange}
              onEnterPress={kernel.current?.triggerGenerate}
              maxHeight={240}
              labelClassName="block text-sm font-medium text-white mb-1"
            />
          </div>

          <AppTooltip content={CFG_ONE_LAST_STEPS_TIP} side="left" contentClassName={TOOLTIP_CLASS}>
            <div className="mb-4">
              <RangeControl
                label="CFG 1 on last steps"
                name="cfgOneLastSteps"
                min={0}
                max={Math.max(0, (params.steps ?? QWEN_IMAGE_21_DEFAULT_STEPS) - 1)}
                step={1}
                value={params.cfgOneLastSteps ?? 0}
                onChange={handleCfgOneLastStepsChange}
              />
            </div>
          </AppTooltip>
        </>
      )}

      <AppTooltip content={NOISE_LEVEL_TIP} side="left" contentClassName={TOOLTIP_CLASS}>
        <div className="mb-4">
          <RangeControl
            label="Noise level"
            name="noiseLevel"
            min={QWEN_IMAGE_21_MIN_NOISE_LEVEL}
            max={1}
            step={0.005}
            value={params.noiseLevel ?? 1}
            onChange={handleNoiseLevelChange}
          />
        </div>
      </AppTooltip>
    </>
  );
});
QwenImage21ControlPanel.displayName = 'QwenImage21ControlPanel';

export default QwenImage21ControlPanel;
